/**
 * AI:
 * Status: "in progress"
 * Type: Logic
 * SubType: QL
 * Reason: The one place where the bm module reads the OPEN applications of a billing account - the
 *         `cw.CwCaseProduct` lines whose Vorgang pays through the account and that have not been
 *         converted into a contract yet - plus the pure reading of the pipe's answer. Feeds the section
 *         „Offene Anträge (noch kein Vertrag)" of the Verträge tab (projects/bm-billing-account-open-cw-cases).
 *
 * <h3>The rule (requirement 01, business rules 1-4)</h3>
 * One row per `cw.CwCaseProduct` with
 * <ul>
 *   <li>`cw.CwCasePayment.customerBillingAccount` = this account - reached in reverse over
 *       `cw.CwCasePayment#cwCase` (1:0..1, unique per case, so it cannot multiply rows);</li>
 *   <li>`contractBindingState IN (UNBOUND, PARTIALLY_BOUND)` - the backend's own definition of an open case
 *       (`CwOpenCaseBmPortImpl`): the line holds no contract yet, or only part of its premium's worth;
 *       a bound line shows up as a contract row instead, never twice. A contract-change line that is
 *       ACCEPTED, has a conversionDate and has no contract link is a change CONFIRMED IN PLACE - it needs no new contract and is
 *       closed, exactly as the backend port treats it;</li>
 *   <li>the Vorgang ACTIVE (`entityStatus = active`);</li>
 *   <li>the Vorgang's phase „Einreichbar" or „Wartet auf Policierung" (user decision mm, 2026-10-06);</li>
 *   <li>the line not ended: `lineOutcome NOT IN (DECLINED, FAILED, CANCELLED, REMOVED)` - see
 *       {@link BM_OPEN_CW_CASE_EXCLUDED_LINE_OUTCOMES}.</li>
 * </ul>
 * No IBAN or other payment value is selected (A14): of `pay` only the id is requested.
 *
 * <h3>Why ordinal literals and no cw import</h3>
 * `CwModule` imports `CrModule`, which imports `BmModule` - a bm → cw import would close that cycle
 * (BOUND-002/BOUND-003). Entity names and aliases are plain strings, and the ordinals below are literals.
 * `bm-open-cw-case-lines-ql.spec.ts` imports the cw enums (a spec may) and asserts every literal against
 * them - that spec is the drift guard these constants cannot be on their own.
 *
 * <h3>Measured on dev/tenantA, 2026-10-06 (read-only)</h3>
 * Under the `simple` pipe every alias arrives FLAT (`pay.id`, `c.processPhaseEnumName`, ...). `INN` with
 * an ARRAY value excludes exactly what the equivalent chain of NE filters excludes (2,547 lines, 5 of them
 * with outcome 6/7: 2,542 either way). A failing query answers HTTP 200 with pipe `status = 1`, empty
 * `data` and a severity-2 message - {@link bmReadOpenCwCaseLines} reports that as a failure, never as
 * „no open applications".
 */
import {FilterCriteria} from '../../../../core/shared/filter/api/filter.criteria';
import {Sorting} from '../../../../core/shared/misc/sorting';
import {QlQueryDto} from '../../../../core/shared/ql/data/dto/ql-query.dto';
import {EntityStatusEnum} from '../../../../core/shared/entity/enum/entity-status.enum';
import type {BmOpenCwCaseLine} from './bm-open-cw-case-line';
import type {QlPipeResultDto} from '../../../../core/shared/ql/data/dto/ql-pipe-result.dto';

/** Name of the query AND of its pipe - the wire name and the read name never drift. */
export const BM_OPEN_CW_CASE_LINES_QUERY = 'bmOpenCwCaseLines';

/**
 * Pipe settings the query needs: `resolveEnums: 'resolved'` makes the phase's label, icon and colours
 * (`c.processPhaseEnumName` / `...EnumImage` / `...EnumColor` / `...EnumColorBackground`) arrive. A caller
 * that declares ANY explicit pipe must declare this one - the builder stops auto-generating pipes then.
 */
export const BM_OPEN_CW_CASE_LINES_PIPE_SETTINGS = {
    includeForm: false,
    includeCount: false,
    resolveEnums: 'resolved'
} as const;

/** Object type the Vorgangsnummer link opens. */
export const BM_CW_CASE_OBJECT_TYPE = 'cw.CwCase';

/** `CwProcessPhaseEnum.SUBMITTABLE` („Einreichbar") - backend `com.mvs.backend.cw.enums.CwProcessPhaseEnum`, 8th. */
export const BM_CW_PROCESS_PHASE_SUBMITTABLE = 7;

/**
 * `CwProcessPhaseEnum.AWAITING_POLICING` („Wartet auf Policierung") - 14th value. NOT append-only-safe:
 * the ordinals shifted once (QUICK_QUOTE inserted at 0 on 2026-08-15, V375__.sql, 12 → 13).
 */
export const BM_CW_PROCESS_PHASE_AWAITING_POLICING = 13;

/** The phases the section shows - and the only ones (user decision mm, 2026-10-06). */
export const BM_OPEN_CW_CASE_PHASES: readonly number[] = [
    BM_CW_PROCESS_PHASE_SUBMITTABLE,
    BM_CW_PROCESS_PHASE_AWAITING_POLICING
];

/** `CwCaseProductOutcomeEnum.DECLINED` („Abgelehnt") - backend `com.mvs.backend.cw.enums.CwCaseProductOutcomeEnum`. */
export const BM_CW_LINE_OUTCOME_DECLINED = 4;

/** `CwCaseProductOutcomeEnum.FAILED` („Fehlgeschlagen"). */
export const BM_CW_LINE_OUTCOME_FAILED = 5;

/** `CwCaseProductOutcomeEnum.CANCELLED` („Storniert"). */
export const BM_CW_LINE_OUTCOME_CANCELLED = 6;

/** `CwCaseProductOutcomeEnum.REMOVED` („Entfernt"). */
export const BM_CW_LINE_OUTCOME_REMOVED = 7;

/**
 * Line outcomes that keep a line OUT of the section.
 *
 * The requirement names the excluded outcomes „storniert / fehlgeschlagen / entfernt" and gives the
 * ordinals „4/6/7" - two readings that disagree: in the enum 4 is DECLINED („Abgelehnt") and
 * „Fehlgeschlagen" is 5. The set is their UNION: a declined line (terminal) will never become a
 * contract, so it is no open application either, and „Fehlgeschlagen" is named explicitly.
 * ACCEPTED (3) stays IN: an accepted line that is not converted yet is exactly an application still
 * waiting for its contract. Measured on dev/tenantA 2026-10-06: no open line in the two phases
 * carries outcome 4 or 5, so the union changes no current row.
 */
export const BM_OPEN_CW_CASE_EXCLUDED_LINE_OUTCOMES: readonly number[] = [
    BM_CW_LINE_OUTCOME_DECLINED,
    BM_CW_LINE_OUTCOME_FAILED,
    BM_CW_LINE_OUTCOME_CANCELLED,
    BM_CW_LINE_OUTCOME_REMOVED
];

/**
 * `CwContractBindingStateEnum.UNBOUND` („Kein Vertrag") - backend `com.mvs.backend.cw.enums.CwContractBindingStateEnum`.
 * The line has no contract link.
 */
export const BM_CW_BINDING_STATE_UNBOUND = 0;

/** `CwContractBindingStateEnum.PARTIALLY_BOUND` („Teilweise zugeordnet") - linked, but not complete yet. */
export const BM_CW_BINDING_STATE_PARTIALLY_BOUND = 1;

/** The binding states that make a line an OPEN application - FULLY_BOUND (2) and CLOSED_BY_REVIEWER (3) do not. */
export const BM_OPEN_CW_CASE_BINDING_STATES: readonly number[] = [
    BM_CW_BINDING_STATE_UNBOUND,
    BM_CW_BINDING_STATE_PARTIALLY_BOUND
];

/** `CwCaseBusinessTypeEnum.contractChange` - the line amends an existing contract instead of concluding a new one. */
export const BM_CW_BUSINESS_TYPE_CONTRACT_CHANGE = 3;

/** `CwCaseProductOutcomeEnum.ACCEPTED` („Angenommen"). */
export const BM_CW_LINE_OUTCOME_ACCEPTED = 3;

/** Typography of the phase tag - the first half of every `phaseStyleClass`. */
export const BM_OPEN_CW_CASE_PHASE_TAG_BASE_CLASS = 'text-xs font-bold';

/** Phase tag colours when the enum's resolved colours are missing or malformed. */
export const BM_OPEN_CW_CASE_NEUTRAL_PHASE_STYLE_CLASS = 'bg-gray-200 text-gray-800';

/** A PrimeFlex palette token as the backend's `@UiEnumValueInfo` stores it, e.g. `purple-800`. */
const COLOR_TOKEN_PATTERN = /^[a-z]+-\d{2,3}$/;

/** `QlPipeResultStatus.error` as it arrives on the wire (ordinal; the name is accepted as well). */
const PIPE_STATUS_ERROR_ORDINAL = 1;
const PIPE_STATUS_ERROR_NAME = 'error';

/** `QlMessageSeverity.error`. */
const MESSAGE_SEVERITY_ERROR = 2;

/** What {@link bmReadOpenCwCaseLines} makes of one pipe answer. */
export interface BmOpenCwCaseLinesRead {
    /** The open lines, newest Vorgang first; empty when the read failed. */
    lines: BmOpenCwCaseLine[];
    /** True when the pipe is missing or reported an error - never inferred from an empty list. */
    failed: boolean;
}

/**
 * The open application lines of one billing account.
 *
 * Root `cw.CwCaseProduct` (one row per line). The case and its payment are INNER joins - a line without
 * a case or without payment data cannot belong to this account. Product, partner, contract type and the
 * line's configuration row are LEFT joins and carry only what the columns show; they mirror the backend
 * header resolution (`CwCaseContractProjectionService.resolveHeaderSources`), so the Typ and Gesellschaft
 * columns say what the contract will carry: contract type line → product → configuration row, partner
 * line → configuration row → product (on dev the line's own contract type is empty on every open line).
 * Sorted by the Vorgang's creation, newest first; the line id keeps the lines of one Vorgang stable.
 */
export function bmOpenCwCaseLinesQuery(accountId: number): QlQueryDto {
    return {
        name: BM_OPEN_CW_CASE_LINES_QUERY,
        type: 'view',
        start: {
            type: 'entity',
            name: 'cw.CwCaseProduct',
            as: 'cp',
            fields: [
                {name: 'id'},
                {name: 'noneDefinedProductText'},
                {name: 'grossPremium'},
                {name: 'currency'},
                {name: 'startDate'},
                {name: 'lineOutcome'},
                {name: 'businessType'},
                {name: 'conversionDate'},
                {name: 'contractBindingState'}
            ],
            joins: [
                {
                    type: 'entity', name: 'cwCase', as: 'c', joinType: 'inner',
                    fields: [{name: 'id'}, {name: 'caseIdentifier'}, {name: 'processPhase'}, {name: 'createdDate'}],
                    joins: [
                        // Zahlungsdaten - reverse 1:0..1; ONLY the id (no IBAN, A14).
                        {
                            type: 'entity', name: 'cw.CwCasePayment#cwCase', as: 'pay', joinType: 'inner',
                            fields: [{name: 'id'}]
                        }
                    ]
                },
                {
                    type: 'entity', name: 'product', as: 'prod', joinType: 'left',
                    fields: [{name: 'id'}, {name: 'name'}],
                    joins: [
                        {
                            type: 'entity', name: 'contractType', as: 'pct', joinType: 'left',
                            fields: [{name: 'id'}, {name: 'name'}]
                        },
                        {
                            type: 'entity', name: 'partner', as: 'pptn', joinType: 'left',
                            fields: [{name: 'id'}, {name: 'name'}]
                        }
                    ]
                },
                {
                    // The configuration row of the line (cw.CwCaseTypeProduct) - the last rung of both fallbacks.
                    type: 'entity', name: 'type', as: 'ctp', joinType: 'left',
                    fields: [{name: 'id'}],
                    joins: [
                        {
                            type: 'entity', name: 'contractType', as: 'tct', joinType: 'left',
                            fields: [{name: 'id'}, {name: 'name'}]
                        },
                        {
                            type: 'entity', name: 'partner', as: 'tptn', joinType: 'left',
                            fields: [{name: 'id'}, {name: 'name'}]
                        }
                    ]
                },
                {
                    type: 'entity', name: 'partner', as: 'ptn', joinType: 'left',
                    fields: [{name: 'id'}, {name: 'name'}]
                },
                {
                    type: 'entity', name: 'contractType', as: 'ct', joinType: 'left',
                    fields: [{name: 'id'}, {name: 'name'}]
                }
            ]
        },
        filters: [
            FilterCriteria.create('pay.customerBillingAccountId', FilterCriteria.cOperatorEqual, accountId),
            FilterCriteria.create('cp.contractBindingState', FilterCriteria.cOperatorIn,
                [...BM_OPEN_CW_CASE_BINDING_STATES]),
            // Not an ACCEPTED contract-change line with a conversionDate and without a link (= confirmed
            // in place; ACCEPTED alone is not enough, an unconfirmed change line can be ACCEPTED too). Written as the
            // positive OR of its negation: `businessType` is nullable, and `<>` on NULL is NULL, so the
            // IS NULL branch is what keeps every ordinary line in. `lineOutcome` is NOT NULL.
            FilterCriteria.createOr(
                FilterCriteria.create('cp.contractBindingState', FilterCriteria.cOperatorEqual,
                    BM_CW_BINDING_STATE_PARTIALLY_BOUND),
                FilterCriteria.create('cp.businessType', FilterCriteria.cOperatorIsNull),
                FilterCriteria.create('cp.businessType', FilterCriteria.cOperatorNotEqual,
                    BM_CW_BUSINESS_TYPE_CONTRACT_CHANGE),
                FilterCriteria.create('cp.lineOutcome', FilterCriteria.cOperatorNotEqual,
                    BM_CW_LINE_OUTCOME_ACCEPTED),
                FilterCriteria.create('cp.conversionDate', FilterCriteria.cOperatorIsNull)
            ),
            FilterCriteria.create('c.entityStatus', FilterCriteria.cOperatorEqual, EntityStatusEnum.active),
            FilterCriteria.create('c.processPhase', FilterCriteria.cOperatorIn, [...BM_OPEN_CW_CASE_PHASES]),
            FilterCriteria.create('cp.lineOutcome', FilterCriteria.cOperatorInNot,
                [...BM_OPEN_CW_CASE_EXCLUDED_LINE_OUTCOMES])
        ],
        sortings: [new Sorting('c.createdDate', false), new Sorting('cp.id', true)]
    };
}

/** A pipe answer as the server sends it: {@link QlPipeResultDto} plus the `status` the DTO does not declare (ordinal or name). */
type BmQlPipeAnswer = Partial<QlPipeResultDto> & { status?: number | string };

/**
 * Reads one answer of the {@link BM_OPEN_CW_CASE_LINES_QUERY} pipe (`QlQueryResult.getPipeResult`).
 *
 * A missing pipe, `status = error` or any error message is a FAILURE (`failed: true`, no lines) - the
 * query answers HTTP 200 with an empty `data` then, which must not read as „no open applications".
 * Rows outside the rule (another phase, an excluded outcome) or without line/case id are dropped even if
 * the server returned them, and a line id is taken once.
 */
export function bmReadOpenCwCaseLines(pipeResult: BmQlPipeAnswer | null | undefined): BmOpenCwCaseLinesRead {

    if (!pipeResult || isFailedPipe(pipeResult)) {
        return {lines: [], failed: true};
    }

    const lines: BmOpenCwCaseLine[] = [];
    const seenLineIds = new Set<number>();

    for (const row of (pipeResult.data ?? [])) {
        const line = readLine(row);
        if (!line || seenLineIds.has(line.lineId)) {
            continue;
        }
        seenLineIds.add(line.lineId);
        lines.push(line);
    }

    return {lines, failed: false};
}

function isFailedPipe(pipeResult: BmQlPipeAnswer): boolean {
    const status = pipeResult.status;
    if (status === PIPE_STATUS_ERROR_ORDINAL || status === PIPE_STATUS_ERROR_NAME) {
        return true;
    }
    return (pipeResult.messages ?? []).some(message => message?.severity === MESSAGE_SEVERITY_ERROR);
}

function readLine(row: Record<string, unknown> | null | undefined): BmOpenCwCaseLine | null {

    const lineId = toNumberOrNull(row?.['cp.id']);
    const caseId = toNumberOrNull(row?.['c.id']);
    const processPhase = toNumberOrNull(row?.['c.processPhase']);
    const lineOutcome = toNumberOrNull(row?.['cp.lineOutcome']);
    const bindingState = toNumberOrNull(row?.['cp.contractBindingState']);
    const businessType = toNumberOrNull(row?.['cp.businessType']);
    const converted = textOrNull(row?.['cp.conversionDate']) != null;

    if (lineId == null || caseId == null || processPhase == null
        || !BM_OPEN_CW_CASE_PHASES.includes(processPhase)
        || (lineOutcome != null && BM_OPEN_CW_CASE_EXCLUDED_LINE_OUTCOMES.includes(lineOutcome))
        || (bindingState != null && !BM_OPEN_CW_CASE_BINDING_STATES.includes(bindingState))
        || (bindingState === BM_CW_BINDING_STATE_UNBOUND
            && businessType === BM_CW_BUSINESS_TYPE_CONTRACT_CHANGE
            && lineOutcome === BM_CW_LINE_OUTCOME_ACCEPTED
            && converted)) {
        return null;
    }

    const currency = textOrNull(row['cp.currency']);
    const caseIdentifier = textOrNull(row['c.caseIdentifier']);

    return {
        lineId,
        caseId,
        caseIdentifier,
        caseLabel: caseIdentifier ?? `Vorgang #${caseId}`,
        caseAriaLabel: `Vorgang ${caseIdentifier ?? caseId} öffnen`,
        productName: firstText(row['prod.name'], row['cp.noneDefinedProductText']),
        partnerName: firstText(row['ptn.name'], row['tptn.name'], row['pptn.name']),
        contractTypeName: firstText(row['ct.name'], row['pct.name'], row['tct.name']),
        grossPremium: toNumberOrNull(row['cp.grossPremium']),
        currencyLabel: !currency || currency.toUpperCase() === 'EUR' ? '€' : currency,
        startDate: textOrNull(row['cp.startDate']),
        processPhase,
        partiallyBound: bindingState === BM_CW_BINDING_STATE_PARTIALLY_BOUND,
        phaseLabel: textOrNull(row['c.processPhaseEnumName']),
        phaseIcon: textOrNull(row['c.processPhaseEnumImage']),
        phaseStyleClass: phaseStyleClass(row['c.processPhaseEnumColorBackground'], row['c.processPhaseEnumColor']),
        createdDate: textOrNull(row['c.createdDate'])
    };
}

/**
 * The complete phase tag styleClass: the tag typography plus `bg-{background} text-{color}` from two
 * palette tokens, or plus the neutral pair when either is unusable.
 */
function phaseStyleClass(background: unknown, color: unknown): string {
    const colours = typeof background === 'string' && typeof color === 'string'
        && COLOR_TOKEN_PATTERN.test(background) && COLOR_TOKEN_PATTERN.test(color)
        ? `bg-${background} text-${color}`
        : BM_OPEN_CW_CASE_NEUTRAL_PHASE_STYLE_CLASS;
    return `${BM_OPEN_CW_CASE_PHASE_TAG_BASE_CLASS} ${colours}`;
}

function firstText(...values: unknown[]): string | null {
    for (const value of values) {
        const text = textOrNull(value);
        if (text) {
            return text;
        }
    }
    return null;
}

function textOrNull(value: unknown): string | null {
    if (value == null) {
        return null;
    }
    const text = String(value).trim();
    return text.length ? text : null;
}

/** Backend Longs / BigDecimals may arrive as numbers or strings; anything unparseable is null. */
function toNumberOrNull(value: unknown): number | null {
    if (value == null || value === '') {
        return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
}
